import 'dayjs/locale/es';

import { Box, FormControl } from '@mui/material';
import dayjs from 'dayjs';
import {
  getPurchaseOrders,
  getSuppliers,
  getTenders,
} from 'lib/api/publicMarket';
import { SuppliersResponse } from 'lib/api/publicMarket/types';
import GlobalSearcher from 'lib/common/GlobalSearcher';
import CustomButton from 'lib/designSystem/Button';
import EnhancedTab from 'lib/designSystem/TabSimulation';
import CustomToggleButtonGroup from 'lib/designSystem/ToggleButtonGroup';
import { errorHandler } from 'lib/functions/errors';
import { PurchaseOrder, Tender } from 'lib/types/models';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import PurchaseOrders from './components/PurchaseOrders';
import Summary from './components/Summary';
import Suppliers from './components/Suppliers';
import Tenders from './components/Tenders';
import { usePublicMarketFilters } from './hooks/usePublicMarketFilters';

dayjs.locale('es');

type Supplier = SuppliersResponse['data'][number];

const BASE_PAGINATION = {
  page: 1,
  pageSize: 25,
  total: 0,
  totalPages: 0,
};

const PublicMarket = () => {
  const dispatch = useDispatch();
  const [openLeadDialog, setOpenLeadDialog] = useState(false);
  const [activeTab, setActiveTab] = React.useState<number>(0);
  const [pagination, setPagination] = useState(BASE_PAGINATION);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [loadingTenders, setLoadingTenders] = useState(false);
  const [loadingPurchaseOrders, setLoadingPurchaseOrders] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [suppliers, setSuppliers] = useState<SuppliersResponse['data']>([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState<Supplier[]>([]);
  const [supplierFilterMode, setSupplierFilterMode] = useState<
    'tender' | 'purchaseOrder'
  >('tender');

  const {
    filters,
    initialDateStr,
    finalDateStr,
    dateOptions,
    externalCode,
    selectedBuyer,
    selectedSupplier,
    status,
    types,
    resetFilters,
  } = usePublicMarketFilters({
    activeTab,
    supplierFilterMode,
  });

  const disableLeadButton = useMemo(() => {
    return activeTab !== 3 || selectedSuppliers?.length === 0;
  }, [activeTab, selectedSuppliers]);

  const buildTendersQuery = (override?: {
    page?: number;
    pageSize?: number;
    useSupplierFilterMode?: boolean;
  }): string => {
    const params = new URLSearchParams();
    if (externalCode) params.append('externalCode', externalCode);
    if (selectedBuyer) params.append('buyerId', selectedBuyer);
    if (types.length > 0)
      params.append('types', types.map((t) => t.value).join(','));

    const shouldUseDateOptions =
      activeTab === 1 || (activeTab === 3 && supplierFilterMode === 'tender');

    if (initialDateStr && shouldUseDateOptions)
      params.append(
        dateOptions === 'publication'
          ? 'startPublicationDate'
          : 'startAdjudicationDate',
        initialDateStr,
      );
    else if (initialDateStr && !shouldUseDateOptions)
      params.append('startCreationDate', initialDateStr);

    if (finalDateStr && shouldUseDateOptions)
      params.append(
        dateOptions === 'publication'
          ? 'endPublicationDate'
          : 'endAdjudicationDate',
        finalDateStr,
      );
    else if (finalDateStr && !shouldUseDateOptions)
      params.append('endCreationDate', finalDateStr);

    if (status.length > 0)
      params.append('status', status.map((s) => s.value).join(','));
    const pageToUse = override?.page ?? pagination.page;
    const pageSizeToUse = override?.pageSize ?? pagination.pageSize;
    if (pageToUse) params.append('page', String(pageToUse));
    if (pageSizeToUse) params.append('pageSize', String(pageSizeToUse));
    return params.toString();
  };

  const fetchTenders = async (params?: { page: number; pageSize: number }) => {
    try {
      setLoadingTenders(true);
      const pageToUse = params?.page ?? pagination.page;
      const pageSizeToUse = params?.pageSize ?? pagination.pageSize;
      const queryString = buildTendersQuery({
        page: pageToUse,
        pageSize: pageSizeToUse,
      });
      const response = await getTenders(dispatch, queryString);
      setTenders(response?.data ?? []);
      setPagination(response?.pagination ?? BASE_PAGINATION);
      return {
        data: response?.data ?? [],
        pagination: response?.pagination ?? BASE_PAGINATION,
      };
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoadingTenders(false);
    }
  };

  const buildPurchaseOrdersQuery = (override?: {
    page?: number;
    pageSize?: number;
  }): string => {
    const params = new URLSearchParams();
    if (externalCode) params.append('externalCode', externalCode);
    if (selectedBuyer) params.append('buyerId', selectedBuyer);
    if (
      selectedSupplier &&
      (activeTab === 2 ||
        (activeTab === 3 && supplierFilterMode === 'purchaseOrder'))
    )
      params.append('supplierId', selectedSupplier);
    if (types.length > 0)
      params.append('types', types.map((t) => t.value).join(','));
    if (status.length > 0)
      params.append('status', status.map((s) => s.value).join(','));
    if (initialDateStr) params.append('startCreationDate', initialDateStr);
    if (finalDateStr) params.append('endCreationDate', finalDateStr);
    const pageToUse = override?.page ?? pagination.page;
    const pageSizeToUse = override?.pageSize ?? pagination.pageSize;
    if (pageToUse) params.append('page', String(pageToUse));
    if (pageSizeToUse) params.append('pageSize', String(pageSizeToUse));
    return params.toString();
  };

  const fetchPurchaseOrders = async (params?: {
    page: number;
    pageSize: number;
  }) => {
    try {
      setLoadingPurchaseOrders(true);
      const pageToUse = params?.page ?? pagination.page;
      const pageSizeToUse = params?.pageSize ?? pagination.pageSize;
      const queryString = buildPurchaseOrdersQuery({
        page: pageToUse,
        pageSize: pageSizeToUse,
      });
      const response = await getPurchaseOrders(dispatch, queryString);
      setPurchaseOrders(response?.data ?? []);
      setPagination(response?.pagination ?? BASE_PAGINATION);
      return {
        data: response?.data ?? [],
        pagination: response?.pagination ?? BASE_PAGINATION,
      };
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoadingPurchaseOrders(false);
    }
  };

  const fetchSuppliers = async (params?: {
    page: number;
    pageSize: number;
  }) => {
    try {
      setLoadingSuppliers(true);
      const pageToUse = params?.page ?? pagination.page;
      const pageSizeToUse = params?.pageSize ?? pagination.pageSize;
      const queryString =
        supplierFilterMode === 'tender'
          ? buildTendersQuery({
              page: pageToUse,
              pageSize: pageSizeToUse,
            })
          : buildPurchaseOrdersQuery({
              page: pageToUse,
              pageSize: pageSizeToUse,
            });
      const response = await getSuppliers(dispatch, queryString);
      setSuppliers(response?.data ?? []);
      setPagination(response?.pagination ?? BASE_PAGINATION);
      return {
        data: response?.data ?? [],
        pagination: response?.pagination ?? BASE_PAGINATION,
      };
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const changeTab = (tab: number) => {
    resetFilters();
    setActiveTab(tab);
    setPagination(BASE_PAGINATION);
  };

  const fetchData = async () => {
    if (activeTab === 1)
      await fetchTenders({
        page: pagination.page,
        pageSize: pagination.pageSize,
      });
    else if (activeTab === 2)
      await fetchPurchaseOrders({
        page: pagination.page,
        pageSize: pagination.pageSize,
      });
    else if (activeTab === 3)
      await fetchSuppliers({
        page: pagination.page,
        pageSize: pagination.pageSize,
      });
  };

  useEffect(() => {
    void fetchData();
  }, [activeTab, supplierFilterMode]);

  return (
    <Box sx={{ position: 'relative' }}>
      <EnhancedTab
        startValue={0}
        tabs={[
          {
            label: 'Resumen',
            component: <Summary />,
          },
          {
            label: 'Licitaciones',
            component: (
              <Tenders
                loading={loadingTenders}
                fetchData={(params?: { page: number; pageSize: number }) =>
                  fetchTenders(params)
                }
                data={tenders}
                setData={setTenders}
                pagination={pagination}
                setPagination={setPagination}
              />
            ),
          },
          {
            label: 'Ordenes de compra',
            component: (
              <PurchaseOrders
                loading={loadingPurchaseOrders}
                fetchData={(params?: { page: number; pageSize: number }) =>
                  fetchPurchaseOrders(params)
                }
                data={purchaseOrders}
                setData={setPurchaseOrders}
                pagination={pagination}
                setPagination={setPagination}
              />
            ),
          },
          {
            label: 'Proveedores',
            component: (
              <Suppliers
                loading={loadingSuppliers}
                data={suppliers}
                setData={setSuppliers}
                pagination={pagination}
                setPagination={setPagination}
                fetchData={(params?: { page: number; pageSize: number }) =>
                  fetchSuppliers(params)
                }
                openLeadDialog={openLeadDialog}
                setOpenLeadDialog={setOpenLeadDialog}
                selectedSuppliers={selectedSuppliers}
                setSelectedSuppliers={setSelectedSuppliers}
              />
            ),
          },
        ]}
        onTabChange={changeTab}
      />
      <Box
        sx={{
          my: 2,
          position: 'absolute',
          top: 0,
          right: 0,
          display: 'flex',
          gap: 2,
          alignItems: 'flex-start',
        }}
      >
        {activeTab === 3 && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <CustomToggleButtonGroup
              options={[
                { label: 'Licitaciones', value: 'tender' },
                { label: 'Ordenes de Compra', value: 'purchaseOrder' },
              ]}
              value={supplierFilterMode}
              onChange={(value) =>
                setSupplierFilterMode(value as 'tender' | 'purchaseOrder')
              }
            />
          </FormControl>
        )}
        {activeTab !== 0 && (
          <GlobalSearcher
            filters={filters}
            triggerSearch={() => fetchData()}
            dispatch={dispatch}
          />
        )}
        {activeTab === 3 && (
          <CustomButton
            color="primary"
            onClick={() => setOpenLeadDialog(true)}
            disabled={disableLeadButton}
          >
            Generar lead
          </CustomButton>
        )}
      </Box>
    </Box>
  );
};

export default PublicMarket;
